import { Component, forwardRef, OnInit, NgZone, Input, ViewChild, SimpleChanges } from '@angular/core';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DocumentPickerComponent } from '@app/components/_admin/file/document-picker/document-picker.component';
import { UploadedFile } from '@app/services/file.service';
import { environment } from '@env/environment';
import { EditorSettings } from 'tinymce';

const EDITOR_COMPONENT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => TextEditorComponent),
  multi: true
};

const defaultToolbar = `
    undo redo | cut copy paste pastetext | bold italic underline strikethrough | fontselect
   | fontsizeselect | forecolor backcolor removeformat | alignleft aligncenter alignright alignjustify | outdent indent
   |  bullist numlist | hr | table | image imagepicker |  documentlink link unlink  |  charmap emoticons | visualblocks code preview`;

const defaultPlugins = `
      lists advlist hr table image imagetools link autolink charmap emoticons
      textpattern quickbars visualblocks preview`;

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'text-editor',
  template: '<editor [init]="init" [ngModel]="value" (ngModelChange)="onChange($event)">',
  providers: [EDITOR_COMPONENT_VALUE_ACCESSOR]
})
export class TextEditorComponent implements OnInit, ControlValueAccessor {

  @Input() init: EditorSettings;

  defaultSettings: EditorSettings = {
    base_url: '/tinymce',
    suffix: '.min',
    image_class_list: [
      { title: 'Responsive', value: 'img-responsive' }
    ],
    plugins: defaultPlugins,
    external_plugins: {
      advcode: '/tinymce/custom-plugins/custom-code/plugin.min.js',
      powerpaste: '/tinymce/custom-plugins/custom-powerpaste/plugin.min.js'
    },
    toolbar: defaultToolbar,
    toolbar_drawer: 'sliding',
    menubar: false,
    contextmenu: 'link image imagetools table',
    quickbars_selection_toolbar: 'bold italic underline | fontsizeselect | forecolor | quicklink | removeformat',
    quickbars_insert_toolbar: false, // 'quickimage quicktable',
    powerpaste_word_import: 'prompt',
    powerpaste_html_import: 'prompt',
    forced_root_block: '', // Uncomment this line causes an error on every "backspace" hit
    convert_urls: false,
    setup: editor => {
      editor.ui.registry.addButton('imagepicker', {
        icon: 'gallery',
        tooltip: 'Ajouter une image de la bibliothèque',
        onAction: () => this.openDocumentPicker('image', (result) => {
          if (result && result.length) {
            const doc = result[0];
            editor.insertContent('<img src="' + this.buildFileUrl(doc.id) + '" title="' + doc.name + '">');
          }
        })
        // onSetup() + editor.on('NodeChange') (like seen in doc https://www.tiny.cloud/docs/ui-components/toolbarbuttons/)
        // would allow us to set button "active" when caret passes on a picked document in editor
      });

      // tslint:disable-next-line: max-line-length
      editor.ui.registry.addIcon('document-link', `<svg height="24" width="24"><path d="M 9.2578125,2 4,7.1113281 V 19.777344 C 3.9997535,21.004601 4.6963484,21.476263 5.6305448,22 h -0.00504 C 4.9530791,20.625831 4.7652669,19.040739 5.9949506,17.889042 V 8.2307072 H 10.422113 V 3.9324506 H 18.00505 V 7.2168003 C 18.616506,7.1077984 19.45409,7.2525134 20,7.6819014 V 4.2226562 C 20.000247,2.9953992 18.977165,2.0002997 17.714844,2 Z M 20,16.396181 c -0.042,0.03954 -0.08312,0.08194 -0.125,0.121094 -1.057893,0.920545 -0.822614,0.804168 -1.86995,1.763672 V 20.06755 H 16.396263 C 15.702675,20.82396 15.324178,21.254264 14.617995,22 h 3.096849 C 18.971986,22 20,20.999566 20,19.777344 Z" /><path fill-rule="nonzero" d="m 9.081332,16.350257 c 0.7845415,-0.589235 1.775672,0.403293 1.187267,1.188941 l -1.780901,1.698488 c -1.7601467,1.037185 1.1907076,4.101644 2.289731,2.377883 l 4.07063,-4.076371 c 0.323227,-0.330223 0.323227,-0.858718 0,-1.188941 -0.916908,-0.528314 0.728967,-2.058783 1.187266,-1.104017 0.904584,0.951153 0.904584,2.445822 0,3.396975 l -4.070629,4.246219 C 8.8632825,25.562055 4.6315739,21.32438 7.300431,18.218593 l 1.696096,-1.698487 z m 9.837355,-0.509547 c -0.784541,0.58923 -1.775667,-0.403294 -1.187266,-1.188941 l 1.696096,-1.698487 c 1.906839,-0.917894 -1.149781,-4.2142806 -2.204925,-2.377883 l -4.070629,4.07637 c -0.323227,0.330223 -0.323227,0.858718 0,1.188941 0.916904,0.528316 -0.728964,2.058781 -1.187267,1.104019 -0.904585,-0.951153 -0.904585,-2.445823 0,-3.396976 l 4.070629,-4.2462142 c 3.101408,-2.6725665 7.333063,1.5650512 4.664264,4.6708352 l -1.696095,1.698488 z"/></svg>`);

      editor.ui.registry.addButton('documentlink', {
        icon: 'document-link',
        tooltip: 'Insérer un lien vers un document',
        onAction: () => this.openDocumentPicker(null, (result) => {
          if (result && result.length) {
            const doc = result[0];
            editor.insertContent('<a href="' + this.buildFileUrl(doc.id) + '">' + doc.name + '</a>');
          }
        })
      });
    },
    language: 'fr_FR',
    language_url: '/assets/i18n/tinymce/fr_FR.js',
    branding: false,
    height: 300
  };

  onChange: (_: any) => {};
  private onTouch: () => {};

  value: any;

  @ViewChild(EditorComponent) editor: EditorComponent;

  constructor(
    private ngZone: NgZone,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    // careful with this, as the "base_url" shouldn't be changed ...
    this.init = { ...this.defaultSettings, ...this.init };
  }

  // Type allows to filter selectable files in DocPicker, onSelected will be called with selection result
  openDocumentPicker(type: string, onSelected: CallableFunction) {

    // After long hours of fails, found the solution there : https://github.com/angular/components/issues/7550
    // => Tiny Editor runs outside of Angular "scope" (Zone), which can be fixed with this simple line
    this.ngZone.run(() => {

      const docPicker = this.dialog.open(DocumentPickerComponent, {
        width: '90%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        data: { type }
      });

      docPicker.afterClosed().subscribe((result: UploadedFile[]) => onSelected(result));
    });
  }

  // Because Tiny doesn't like relative URLs and can't make a proper absolute one ...
  buildFileUrl(id) {
    return environment.apiUrl + 'files/' + id;
  }

  writeValue(obj: any): void {
    this.value = obj;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.editor.setDisabledState(isDisabled);
  }
}
